import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE from "./config";
import "./css/PortalImport.css";

const fmtBytes = (b) => {
  if (!b || isNaN(b)) return "";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  return `${(b / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

// Browse UCSC genomes (hgdownload goldenPath) live and download selected files.
const UcscImportPage = () => {
  const navigate = useNavigate();
  const [genomeQuery, setGenomeQuery] = useState("");
  const [genomes, setGenomes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingGenomes, setLoadingGenomes] = useState(false);
  const [genome, setGenome] = useState(null); // {db, organism, ...}
  const [category, setCategory] = useState("bigZips");

  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileFilter, setFileFilter] = useState("");
  const [selected, setSelected] = useState({}); // url -> true

  const [projectName, setProjectName] = useState("");
  const [launching, setLaunching] = useState(false);
  const [message, setMessage] = useState(null); // {type,text}

  // Load the genome list (and categories) once, filtered client-side as the user types.
  useEffect(() => {
    (async () => {
      setLoadingGenomes(true);
      try {
        const res = await axios.get(`${API_BASE}/api/public-data/ucsc/genomes`, { withCredentials: true });
        setGenomes(res.data.genomes || []);
        setCategories(res.data.categories || []);
      } catch (_) {
        setMessage({ type: "error", text: "Could not reach UCSC. Please try again." });
      } finally { setLoadingGenomes(false); }
    })();
  }, []);

  const shownGenomes = (() => {
    const q = genomeQuery.trim().toLowerCase();
    const list = q
      ? genomes.filter((g) => [g.db, g.organism, g.scientificName, g.description].some((s) => String(s).toLowerCase().includes(q)))
      : genomes;
    return list.slice(0, 300);
  })();

  const loadFiles = async (db, cat) => {
    setLoadingFiles(true); setFiles([]); setSelected({});
    try {
      const res = await axios.get(`${API_BASE}/api/public-data/ucsc/files`, { params: { db, category: cat }, withCredentials: true });
      setFiles(res.data.files || []);
    } catch (_) {
      setMessage({ type: "error", text: "Could not list files for this selection." });
    } finally { setLoadingFiles(false); }
  };

  const pickGenome = (g) => { setGenome(g); setMessage(null); loadFiles(g.db, category); };
  const changeCategory = (c) => { setCategory(c); if (genome) loadFiles(genome.db, c); };

  const shownFiles = fileFilter.trim()
    ? files.filter((f) => f.name.toLowerCase().includes(fileFilter.trim().toLowerCase()))
    : files;
  const selectedUrls = Object.keys(selected).filter((u) => selected[u]);

  const download = async () => {
    if (!projectName.trim()) { setMessage({ type: "error", text: "Enter a project name." }); return; }
    if (!/^[A-Za-z0-9_]+$/.test(projectName)) { setMessage({ type: "error", text: "Project name may only contain letters, numbers, and underscores." }); return; }
    if (!selectedUrls.length) { setMessage({ type: "error", text: "Select at least one file." }); return; }
    if (selectedUrls.length > 50) { setMessage({ type: "error", text: "Maximum 50 files per request." }); return; }
    setLaunching(true); setMessage(null);
    try {
      const res = await axios.post(`${API_BASE}/api/public-data/import`,
        { source: "ucsc", projectName: projectName.trim(), ids: selectedUrls },
        { withCredentials: true });
      setMessage({ type: "success", text: `Download started for ${res.data.summary?.okCount || selectedUrls.length} file(s). Redirecting to your downloads…` });
      setSelected({});
      setTimeout(() => navigate("/downloads", { state: { message: "Your download request has been submitted." } }), 900);
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.error || "Failed to start download." });
    } finally { setLaunching(false); }
  };

  return (
    <div className="portal-import">
      <h1>Download UCSC Data</h1>
      <p className="portal-intro">Browse the UCSC Genome Browser downloads (hgdownload goldenPath) live and download any files to your workspace.</p>

      {/* Step 1: genome */}
      <div className="portal-step">
        <div className="portal-step-title"><span className="portal-step-num">1</span>Choose a genome assembly</div>
        <div className="portal-controls">
          <input className="portal-search" placeholder="Search organism or assembly (e.g. human, hg38, mouse)"
            value={genomeQuery} onChange={(e) => setGenomeQuery(e.target.value)} />
          {loadingGenomes && <span className="portal-spinner" />}
        </div>
        {genome ? (
          <div className="portal-muted">Selected: <strong>{genome.db}</strong> — {genome.organism} ({genome.scientificName}) ·{" "}
            <button className="portal-linkbtn" onClick={() => { setGenome(null); setFiles([]); setSelected({}); }}>change</button>
          </div>
        ) : (
          <div className="portal-list">
            {shownGenomes.map((g) => (
              <div key={g.db} className="portal-row" onClick={() => pickGenome(g)}>
                <span><strong>{g.db}</strong> — {g.organism}<div className="portal-row-sub">{g.scientificName} · {g.description}</div></span>
              </div>
            ))}
            {!shownGenomes.length && !loadingGenomes && <div className="portal-file portal-muted">No matching genomes.</div>}
          </div>
        )}
      </div>

      {/* Step 2 + 3: category + files */}
      {genome && (
        <div className="portal-step">
          <div className="portal-step-title"><span className="portal-step-num">2</span>Choose a data category & files</div>
          <div className="portal-controls">
            <select className="portal-select" value={category} onChange={(e) => changeCategory(e.target.value)}>
              {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <input className="portal-search" placeholder="Filter files by name" value={fileFilter} onChange={(e) => setFileFilter(e.target.value)} />
            {loadingFiles && <span className="portal-spinner" />}
          </div>
          <div className="portal-count">{selectedUrls.length} selected · {shownFiles.length} file(s) shown</div>
          <div className="portal-list">
            {shownFiles.map((f) => (
              <div className="portal-file" key={f.url}>
                <label>
                  <input type="checkbox" checked={!!selected[f.url]} onChange={() => setSelected((p) => ({ ...p, [f.url]: !p[f.url] }))} />
                  {f.name}
                </label>
                <span className="portal-file-size">{fmtBytes(f.size)}</span>
              </div>
            ))}
            {!shownFiles.length && !loadingFiles && <div className="portal-file portal-muted">No files here.</div>}
          </div>
        </div>
      )}

      {/* Actions */}
      {genome && (
        <div className="portal-actions">
          <input className="portal-project" placeholder="Project name (a-z, 0-9, _)" value={projectName} onChange={(e) => setProjectName(e.target.value)} />
          <button className="portal-btn" disabled={launching || !selectedUrls.length} onClick={download}>
            {launching ? "Starting…" : `Download ${selectedUrls.length || ""} file(s)`}
          </button>
          {message && <span className={message.type === "error" ? "portal-error" : "portal-success"}>{message.text}</span>}
        </div>
      )}
    </div>
  );
};

export default UcscImportPage;
